import type { ApiRequestInit } from '../http';

/**
 * Shapes of the backend configuration API (`/v1/config/*`, backend
 * src/modules/configuration). Ported from portal-ui-lib's `configuration.types.ts` and
 * `layout-config.types.ts`; the backend is the source of truth for both.
 */

/**
 * How a GLOBAL admin is looking at the selected org node: `admin` keeps the admin bypass,
 * `scoped` renders the node exactly as one of its regular users would see it.
 */
export type ScopeViewMode = 'admin' | 'scoped';

/**
 * Who is asking, and for which org node. Part of every query key below, so a login, a scope
 * switch or a view-mode toggle can never be answered from another context's cache.
 */
export interface ConfigurationScope {
  userId: string;
  /** The selected org node. Null before one is chosen; feature configuration waits for it. */
  orgNodeId: string | null;
  viewMode: ScopeViewMode;
}

/**
 * An authenticated API call: the logged-in user's session (cookies, CSRF, one refresh on 401)
 * and the selected org scope. `SessionClient.json` (../http) is one; injected rather than
 * imported so the configuration does not depend on how the session is held.
 */
export type ConfigurationApiClient = <T = unknown>(
  path: string,
  init?: ApiRequestInit,
) => Promise<T>;

/** The five summary booleans the backend computes for every resolution. */
export interface CapabilitySummary {
  view: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  export: boolean;
}

export type SummaryCapability = keyof CapabilitySummary;

export const SUMMARY_CAPABILITIES: readonly SummaryCapability[] = [
  'view',
  'create',
  'update',
  'delete',
  'export',
];

export type FeatureAvailabilityReason = 'tenant_disabled' | 'orgnode_disabled' | 'available';

/** `GET /config/resolve` — one feature's configuration for one user at one org node. */
export interface ResolvedConfiguration {
  /**
   * Fully resolved: texts are already translated into the user's language, and blocks whose
   * `_requires` permission the user lacks are already removed. Keys vary by feature — narrow
   * with the helpers in ./presentation.ts before deep use.
   */
  presentation: Record<string, unknown> | null;
  capabilities: CapabilitySummary & {
    /** The granted Permission strings (e.g. `template:read`). */
    capabilities?: string[];
  };
  /** From global preferences; applied as `data-theme` on the document root. */
  theme: string;
  /**
   * From global preferences. Deliberately not used for display: user-visible dates are always
   * Danish `dd-MM-yyyy` (CLAUDE.md), whatever this says. Kept so the type matches the wire.
   */
  dateFormat: string;
  meta: {
    featureKey: string;
    schemaVersion: string;
    version: number;
    resolvedAt: string;
    fallbackMode?: string;
    warnings?: string[];
    featureAvailability?: {
      available: boolean;
      reason: FeatureAvailabilityReason;
      source: 'tenant' | 'orgnode' | 'default' | 'global_admin_override';
    };
    provenance: {
      source: string;
      reason?: string;
      language: string;
      tiers: {
        tier: string;
        orgNodeId?: string;
        userId?: string;
        applied: boolean;
        reason?: string;
      }[];
    };
  };
}

/** A sidebar/header entry. Labels arrive translated. */
export interface LayoutNavItem {
  id: string;
  label: string;
  route?: string;
  externalUrl?: string;
  icon?: string;
  order?: number;
  children?: LayoutNavItem[];
  enabled?: boolean;
  visible?: boolean;
  featureKey?: string;
}

export interface SidebarSection {
  id: string;
  label: string;
  items: LayoutNavItem[];
  order?: number;
  enabled?: boolean;
  visible?: boolean;
  /** Render as a flat list: no group header, no collapse toggle. */
  flat?: boolean;
  /** The section's overview page; its heading links there. */
  route?: string;
}

export interface HeaderLayoutConfig {
  scopeNavigation?: { visible: boolean; enabled: boolean };
  orgNodeSelector?: { visible: boolean; enabled: boolean };
  userDropdown?: { visible: boolean; enabled: boolean; items: LayoutNavItem[] };
}

/**
 * `GET /config/layout` — the app's navigation, already filtered server-side by app
 * membership (`X-App-Id`, backend layout-app-map.ts), entitlements and permissions.
 */
export interface LayoutResponse {
  layout: {
    sidebarSections: SidebarSection[];
    headerItems: LayoutNavItem[];
    headerConfig?: HeaderLayoutConfig;
  };
  /** The user's capability summary, independent of any one feature. */
  capabilities: CapabilitySummary;
  meta: {
    generatedAt: string;
    /** The user's language; what every translated label in this response is in. */
    language: string;
    capabilityHash: string;
    orgNodeId: string;
    tenantId: string;
    featureKeys: string[];
    isGlobalAdmin: boolean;
  };
}
